/* tslint:disable */
import { Matchs, Tour, Participation } from "./index";
import { Timestamp } from "firebase/firestore";
import { normalizeBoardLiveColor } from "../utils/boardLiveColor";

export interface Tableau {
  label: string;
  name: string;
  heureLimite: string;
  nbPlace: number;
  realNbPlaces: number;
  date: Date;
  sexe: Array<string>;
  cat: Array<string>;
  handi: string;
  classe?: Array<string>;
  min?: number;
  max?: number;
  obligatoire?: boolean;
  id?: any;
  matchs?: Array<Matchs>;
  tour?: Array<Tour>;
  participation?: Array<Participation>;
  ferme: boolean;
  hasWaitingQueue: boolean;
  fullPrice?: boolean;
  /**
   * Si `false`, ce tableau ne compte pas dans la limite de 2 inscriptions par jour.
   * Absent ou `true` : compte (rétrocompat).
   */
  comptePourLimiteJournaliere?: boolean;
  /**
   * Si `true`, affiche une info sur la page de saisie de licence :
   * ce tableau s’inscrit / se constitue sur place (ex. non licenciées, loisirs).
   */
  inscriptionSurPlace?: boolean;
  /**
   * Si `true`, ce tableau ne compte pas dans la grille tarifaire (ex. H poussines OFFERT).
   * Seul un ensemble de tableaux offerts → inscription sans Stripe.
   */
  offert?: boolean;
  /** Identifiants des autres tableaux incompatibles (relation symétrique, maintenue côté admin). */
  incompatibleBoardIds?: string[];
  tournoiId?: any;
  nbParticipants: number;
  /** Dénormalisé : taille de la sous-collection waitingList (lecture publique via doc tableau). */
  waitlistCount?: number;
  isFull?: boolean;
  /** Couleur de fond des blocs joueurs sur l'écran live (#RRGGBB). */
  liveColor?: string;
}

export type TableauFirestore = Omit<Tableau, "id" | "date" | "isFull"> & {
  date: Timestamp;
};

/** Normalise une valeur Firestore legacy (string) ou number vers un entier ≥ 0. */
export function normalizeNbPlace(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.max(0, Math.floor(value));
  }
  if (typeof value === "string") {
    const n = parseInt(value, 10);
    return Number.isNaN(n) ? 0 : Math.max(0, n);
  }
  return 0;
}

function normalizeRealNbPlaces(raw: unknown, fallback: number): number {
  const n = normalizeNbPlace(raw);
  return n > 0 ? n : fallback;
}

export function normalizeWaitlistCount(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.max(0, Math.floor(value));
  }
  return 0;
}

export function computePlacesRestantes(
  tableau: Pick<Tableau, "nbPlace" | "realNbPlaces" | "nbParticipants">
): number {
  const nbPlace = normalizeNbPlace(tableau.nbPlace);
  const realNbPlaces = normalizeRealNbPlaces(tableau.realNbPlaces, nbPlace);
  const nbParticipants = normalizeNbPlace(tableau.nbParticipants);
  return Math.max(0, realNbPlaces - nbParticipants);
}

/**
 * Inscription directe bloquée ssi placesRestantes <= waitlistCount (file active uniquement).
 */
export function computeIsFull(
  tableau: Pick<
    Tableau,
    | "nbPlace"
    | "realNbPlaces"
    | "nbParticipants"
    | "waitlistCount"
    | "hasWaitingQueue"
  >
): boolean {
  const placesRestantes = computePlacesRestantes(tableau);
  const waitlistCount =
    tableau.hasWaitingQueue === false
      ? 0
      : normalizeWaitlistCount(tableau.waitlistCount);
  return !(placesRestantes > waitlistCount);
}

/**
 * Construit un `Tableau` typé depuis les données brutes Firestore (rétrocompat string/number).
 */
export function tableauFromFirestoreData(
  id: string,
  raw: Record<string, unknown>
): Tableau {
  const dateField = raw.date as Timestamp | undefined;
  const date =
    dateField && typeof dateField.toDate === "function"
      ? dateField.toDate()
      : new Date();

  const nbPlace = normalizeNbPlace(raw.nbPlace);
  const realNbPlaces = normalizeRealNbPlaces(raw.realNbPlaces, nbPlace);

  const nbParticipants =
    typeof raw.nbParticipants === "number" && Number.isFinite(raw.nbParticipants)
      ? Math.max(0, Math.floor(raw.nbParticipants))
      : 0;
  const waitlistCount = normalizeWaitlistCount(raw.waitlistCount);
  const hasWaitingQueue = raw.hasWaitingQueue !== false;

  const boardBase = {
    id,
    name: String(raw.name ?? ""),
    label: String(raw.label ?? ""),
    heureLimite: String(raw.heureLimite ?? ""),
    nbPlace,
    realNbPlaces,
    date,
    sexe: Array.isArray(raw.sexe) ? (raw.sexe as string[]) : [],
    cat: Array.isArray(raw.cat) ? (raw.cat as string[]) : [],
    handi: String(raw.handi ?? ""),
    classe: Array.isArray(raw.classe) ? (raw.classe as string[]) : undefined,
    min: typeof raw.min === "number" ? raw.min : undefined,
    max: typeof raw.max === "number" ? raw.max : undefined,
    obligatoire: raw.obligatoire === true,
    ferme: raw.ferme === true,
    hasWaitingQueue,
    fullPrice: raw.fullPrice === true,
    comptePourLimiteJournaliere:
      raw.comptePourLimiteJournaliere === false ? false : true,
    inscriptionSurPlace: raw.inscriptionSurPlace === true,
    offert: raw.offert === true,
    incompatibleBoardIds: Array.isArray(raw.incompatibleBoardIds)
      ? (raw.incompatibleBoardIds as unknown[]).filter(
          (x): x is string => typeof x === "string" && x.length > 0
        )
      : undefined,
    liveColor: normalizeBoardLiveColor(raw.liveColor),
    tournoiId: raw.tournoiId,
    nbParticipants,
    waitlistCount,
  };

  return {
    ...boardBase,
    isFull: computeIsFull(boardBase),
  };
}

export function mapTableauToFirestore(tableau: Tableau): TableauFirestore {
  const { id, isFull, ...rest } = tableau;
  return {
    ...rest,
    date: Timestamp.fromDate(tableau.date),
  };
}

function coerceBoardDate(raw: unknown): Date {
  if (raw instanceof Date) return raw;
  if (typeof raw === "string") return new Date(raw);
  if (
    raw &&
    typeof (raw as { toDate?: () => Date }).toDate === "function"
  ) {
    return (raw as { toDate: () => Date }).toDate();
  }
  return new Date();
}

/**
 * Normalise un tableau renvoyé par une Cloud Function (JSON) vers le type `Tableau`.
 */
export function normalizeTableauFromCallable(board: unknown): Tableau {
  const r = board as Record<string, unknown>;
  const id = String(r.id ?? "");
  const date = coerceBoardDate(r.date);
  return tableauFromFirestoreData(id, {
    ...r,
    date: Timestamp.fromDate(date),
  });
}
